import type {
  DataAuditAuditTableParams,
  DataAuditQueuedTableParams,
  DataAuditWritebackStatusApiType,
  DataAuditWritebackType,
} from 'src/components/data-audit/types';
import type {DateRangeWithTimezone} from '@spaced-out/ui-design-system/lib/types';
import {DATA_AUDIT_ALL_FILTER_KEY} from 'src/components/data-audit/data-audit-filters';

import {DATA_AUDIT_AUDIT_TABLE_PAGE_SIZE} from './constants';
import {getDataAuditDateRangeBounds} from './date-range';


export function buildDataAuditAuditTableParams(input: {
  writebackType: DataAuditWritebackType;
  statusFilter: string;
  appliedFilters: {
    entity: string;
    note: string;
    source: string;
  };
  dateRange: DateRangeWithTimezone;
  search: string;
  page: number;
  sort: 'asc' | 'desc';
}): DataAuditAuditTableParams {
  const {from, to} = getDataAuditDateRangeBounds(input.dateRange);

  const params: DataAuditAuditTableParams = {
    writebackType: input.writebackType,
    from,
    to,
    page: input.page,
    size: DATA_AUDIT_AUDIT_TABLE_PAGE_SIZE,
    sort: input.sort,
  };

  if (input.statusFilter !== DATA_AUDIT_ALL_FILTER_KEY) {
    params.status = input.statusFilter;
  }

  if (input.appliedFilters.source !== DATA_AUDIT_ALL_FILTER_KEY) {
    params.source = input.appliedFilters.source;
  }

  if (
    (input.writebackType === 'owb' || input.writebackType === 'fwb') &&
    input.appliedFilters.entity !== DATA_AUDIT_ALL_FILTER_KEY
  ) {
    params.entityType = input.appliedFilters.entity;
  }

  if (
    input.writebackType === 'nwb' &&
    input.appliedFilters.note !== DATA_AUDIT_ALL_FILTER_KEY
  ) {
    params.noteType = input.appliedFilters.note;
  }

  const trimmedSearch = input.search.trim();
  if (trimmedSearch) {
    params.search = trimmedSearch;
  }

  return params;
}

export function buildDataAuditAuditTableQuery(
  params: DataAuditAuditTableParams,
): Record<string, string> {
  const query: Record<string, string> = {
    writebackType: params.writebackType.toUpperCase(),
    page: String(params.page),
    size: String(params.size),
  };

  if (params.status) {
    query.status = params.status;
  }

  if (params.source) {
    query.source = params.source;
  }

  if (params.entityType) {
    query.entityType = params.entityType;
  }

  if (params.noteType) {
    query.noteType = params.noteType;
  }

  if (params.from) {
    query.from = params.from;
  }

  if (params.to) {
    query.to = params.to;
  }

  if (params.search) {
    query.search = params.search;
  }

  if (params.sort) {
    query.sort = params.sort;
  }

  return query;
}

export function buildDataAuditQueuedTableParams(input: {
  writebackType: DataAuditWritebackType;
  appliedFilters: {
    entity: string;
    note: string;
    source: string;
  };
  search: string;
  page: number;
}): DataAuditQueuedTableParams {
  const params: DataAuditQueuedTableParams = {
    writebackType: input.writebackType,
    page: input.page,
    size: DATA_AUDIT_AUDIT_TABLE_PAGE_SIZE,
  };

  if (input.appliedFilters.source !== DATA_AUDIT_ALL_FILTER_KEY) {
    params.source = input.appliedFilters.source;
  }

  if (
    (input.writebackType === 'owb' || input.writebackType === 'fwb') &&
    input.appliedFilters.entity !== DATA_AUDIT_ALL_FILTER_KEY
  ) {
    params.atsEntityType = input.appliedFilters.entity;
  }

  if (
    input.writebackType === 'nwb' &&
    input.appliedFilters.note !== DATA_AUDIT_ALL_FILTER_KEY
  ) {
    params.noteType = input.appliedFilters.note;
  }

  const trimmedSearch = input.search.trim();
  if (trimmedSearch) {
    params.search = trimmedSearch;
  }

  return params;
}

export function buildDataAuditQueuedTableQuery(
  params: DataAuditQueuedTableParams,
): Record<string, string> {
  const query: Record<string, string> = {
    writebackType: params.writebackType.toUpperCase(),
    page: String(params.page),
    size: String(params.size),
  };

  if (params.source) {
    query.source = params.source;
  }

  if (params.atsEntityType) {
    query.atsEntityType = params.atsEntityType;
  }

  if (params.noteType) {
    query.noteType = params.noteType;
  }

  if (params.search) {
    query.search = params.search;
  }

  return query;
}

export function getDataAuditQueuedTableEndpoint(): string {
  return 'writebacks-summary/queued';
}

export function getWritebackAuditStatusApiType(
  writebackType: DataAuditWritebackType,
): DataAuditWritebackStatusApiType {
  return writebackType === 'nwb' ? 'nwb' : 'owb';
}
